import React, { useState, useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import SearchSelectBox, {
  ISelectOption,
} from '@/components/ui/SearchSelectBox';

import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { formatDateToInput, showToastNotification } from '@/utility/helper';
import { IEmployeeOnBoardResponse } from '@/interfaces/employee.model';
import {
  IOffboardingFormInputs,
  IOffboardingResponse,
} from '@/interfaces/offbording.model';
import { IUser } from '@/interfaces/user.model';

interface OffboardingDetailsStepProps {
  employeeData: IUser | null;
  userId: string;
  setOffboardingId: (id: string) => void;
  setEmployeeDetails: (details: Partial<IOffboardingFormInputs>) => void;
  nextStep: () => void;
  isEditMode?: boolean;
  offboardingId?: string;
  initialValues?: Partial<IOffboardingFormInputs>;
  isCompleted?: boolean;
}

const OffboardingDetailsStep: React.FC<OffboardingDetailsStepProps> = ({
  employeeData,
  userId,
  setOffboardingId,
  setEmployeeDetails,
  nextStep,
  isEditMode = false,
  offboardingId = '',
  initialValues = {},
  isCompleted = false,
}) => {
  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IOffboardingFormInputs>({
    mode: 'onChange',
    defaultValues: {
      reason: initialValues.reason || '',
      offBoardType: initialValues.offBoardType || undefined,
      joiningDate: formatDateToInput(initialValues.joiningDate),
      lastWorkingDay: formatDateToInput(initialValues.lastWorkingDay),
      tenure:
        initialValues.tenure !== undefined
          ? String(initialValues.tenure)
          : undefined,
      handoverId: initialValues.handoverId || '',
      assetHandover: initialValues.assetHandover || false,
      isCompletedProject: initialValues.isCompletedProject || false,
    },
  });

  const joiningDate = useWatch({ control, name: 'joiningDate' });
  const lastWorkingDay = useWatch({ control, name: 'lastWorkingDay' });

  const [employeeOptions, setEmployeeOptions] = useState<ISelectOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const calculateTenure = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    if (diffTime > 0) {
      const diffDays = diffTime / (1000 * 60 * 60 * 24);
      const years = diffDays / 365.25;
      const rounded = Math.round(years * 10) / 10;
      setValue('tenure', String(rounded));
    } else {
      setValue('tenure', '0');
    }
  };

  useEffect(() => {
    if (!isEditMode && employeeData?.joiningDate) {
      const formatted = formatDateToInput(employeeData.joiningDate);
      if (formatted) {
        setValue('joiningDate', formatted);
      }
    }
  }, [employeeData, setValue, isEditMode]);

  useEffect(() => {
    if (joiningDate && lastWorkingDay) {
      calculateTenure(joiningDate, lastWorkingDay);
    }
  }, []);

  useEffect(() => {
    if (joiningDate && lastWorkingDay) {
      calculateTenure(joiningDate, lastWorkingDay);
    } else {
      setValue('tenure', '');
    }
  }, [joiningDate, lastWorkingDay, setValue]);

  const getEmployeeList = async () => {
    try {
      setLoadingEmployees(true);
      const response = (await callApi(
        `${API_URL}${API_ENDPOINT.getEmployee}?status=current`,
        {
          method: 'GET',
        },
      )) as IEmployeeOnBoardResponse;
      if (response?.status) {
        const options =
          response?.data?.user?.map((item) => ({
            label: item?.name,
            value: item?.id,
          })) || [];
        setEmployeeOptions(options);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setLoadingEmployees(false);
    }
  };

  useEffect(() => {
    getEmployeeList();
  }, []);

  const onSubmit = async (data: IOffboardingFormInputs) => {
    if (isCompleted) return;
    if (!userId) {
      showToastNotification('danger', 'No employee selected');
      return;
    }
    if (!data.handoverId) {
      showToastNotification('danger', 'Please select a handover person');
      return;
    }

    setIsSubmitting(true);
    try {
      const tenureNumber =
        typeof data.tenure === 'string'
          ? parseFloat(data.tenure)
          : data.tenure || 0;

      const payload = {
        reason: data.reason,
        OffBoardType: data.offBoardType,
        joiningDate: data.joiningDate,
        lastWorkingDay: data.lastWorkingDay,
        tenure: tenureNumber,
        assetHandover: data.assetHandover || false,
        isCompletedProject: data.isCompletedProject || false,
        handoverId: data.handoverId,
      };

      let response;
      if (isEditMode && offboardingId) {
        const url = `${API_URL}${API_ENDPOINT.offboardUser}/${offboardingId}`;
        response = (await callApi(url, {
          method: 'PUT',
          data: payload,
        })) as IOffboardingResponse;
      } else {
        response = (await callApi(
          `${API_URL}${API_ENDPOINT.offboardUser}/${userId}`,
          {
            method: 'POST',
            data: payload,
          },
        )) as IOffboardingResponse;
      }

      if (response?.status) {
        showToastNotification(
          'success',
          isEditMode
            ? 'Offboarding updated successfully'
            : 'Offboarding initiated successfully',
        );
        const id = isEditMode ? offboardingId : response.data?.id;
        if (id) {
          setOffboardingId(id);
          setEmployeeDetails({ userId, ...payload });
          nextStep();
        } else {
          showToastNotification('danger', 'Offboarding ID missing');
        }
      } else {
        showToastNotification(
          'danger',
          response?.message || 'Operation failed',
        );
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isCompleted && (
        <div className="alert alert-info mb-4" role="alert">
          <i className="bi bi-info-circle me-2"></i>
          This offboarding is already <strong>Completed</strong>. All fields are
          read-only.
        </div>
      )}

      <div className="row g-4">
        <div className="col-md-6">
          <label className="form-label">Reason *</label>
          <input
            type="text"
            className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
            {...register('reason', { required: 'Reason is required' })}
            disabled={isCompleted}
          />
          {errors.reason && (
            <div className="invalid-feedback">{errors.reason.message}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Offboarding Type *</label>
          <select
            className={`form-select ${errors.offBoardType ? 'is-invalid' : ''}`}
            {...register('offBoardType', {
              required: 'Offboarding type is required',
            })}
            disabled={isCompleted}
          >
            <option value="">Select Type</option>
            <option value="Self Resign">Self Resign</option>
            <option value="Terminate">Terminate</option>
          </select>
          {errors.offBoardType && (
            <div className="invalid-feedback">
              {errors.offBoardType.message}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Joining Date *</label>
          <input
            type="date"
            className={`form-control ${errors.joiningDate ? 'is-invalid' : ''}`}
            {...register('joiningDate', {
              required: 'Joining date is required',
            })}
            disabled={true} // read‑only, pre‑filled
          />
          {errors.joiningDate && (
            <div className="invalid-feedback">{errors.joiningDate.message}</div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Last Working Day *</label>
          <input
            type="date"
            className={`form-control ${errors.lastWorkingDay ? 'is-invalid' : ''}`}
            {...register('lastWorkingDay', {
              required: 'Last working day is required',
              validate: (value) => {
                if (joiningDate && value) {
                  const start = new Date(joiningDate);
                  const end = new Date(value);
                  return (
                    end > start || 'Last working day must be after joining date'
                  );
                }
                return true;
              },
            })}
            disabled={isCompleted}
          />
          {errors.lastWorkingDay && (
            <div className="invalid-feedback">
              {errors.lastWorkingDay.message}
            </div>
          )}
        </div>

        <div className="col-md-6">
          <label className="form-label">Tenure (years)</label>
          <input
            type="number"
            step="0.1"
            className={`form-control ${errors.tenure ? 'is-invalid' : ''}`}
            {...register('tenure', { required: 'Tenure is required' })}
            disabled={true} // auto‑calculated
          />
          {errors.tenure && (
            <div className="invalid-feedback">{errors.tenure.message}</div>
          )}
          <small className="text-muted">Auto‑calculated from dates</small>
        </div>

        <div className="col-md-6">
          <label className="form-label">Handover Person *</label>
          <Controller
            name="handoverId"
            control={control}
            rules={{ required: 'Handover person is required' }}
            render={({ field }) => (
              <SearchSelectBox
                value={field.value}
                title="Select Handover Person"
                onChange={(option) => field.onChange(option?.value || '')}
                options={employeeOptions}
                loading={loadingEmployees}
                disable={isSubmitting || isCompleted}
                isAsync={false}
                isUsingFilter={false}
              />
            )}
          />
          {errors.handoverId && (
            <div className="text-danger small">{errors.handoverId.message}</div>
          )}
        </div>

        <input type="hidden" {...register('assetHandover')} />
        <input type="hidden" {...register('isCompletedProject')} />

        <div className="col-12 text-end mt-4">
          <button
            type="submit"
            className="btn btn-primary px-5"
            disabled={isSubmitting || isCompleted}
          >
            {isSubmitting
              ? isEditMode
                ? 'Updating...'
                : 'Initiating...'
              : isEditMode
                ? 'Update Offboarding'
                : 'Initiate Offboarding'}
          </button>
        </div>
      </div>
    </form>
  );
};

export default OffboardingDetailsStep;
